/**
 * Safe merge after create/delete payment link — never replace a full enriched order
 * with the sparse ldsOrderById row returned by /getLDSPaymentUrl.
 */
import { resolveLdsFinancialState } from "./resolveLdsFinancialState.js";

export const LDS_PAYMENT_LINK_ORDER_MERGE_TRACE_TAG =
  "[LDS_PAYMENT_LINK_ORDER_MERGE_TRACE]";

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function isNonemptyObject(value) {
  return isPlainObject(value) && Object.keys(value).length > 0;
}

function hasUsablePartialDisplay(value) {
  if (!isPlainObject(value)) return false;
  return (
    value.display_total != null ||
    value.display_paid != null ||
    value.display_paid_actual != null ||
    value.display_remaining != null
  );
}

function hasUsableUpdatedTotal(value) {
  return isPlainObject(value) && value.display_total_updated != null;
}

function pickBlock(obj, snakeKey, camelKey) {
  if (!obj || typeof obj !== "object") return undefined;
  if (obj[snakeKey] != null) return obj[snakeKey];
  if (camelKey && obj[camelKey] != null) return obj[camelKey];
  return undefined;
}

function preserveBlock(merged, prev, response, snakeKey, camelKey, isUsable) {
  const prevBlock = pickBlock(prev, snakeKey, camelKey);
  const respBlock = pickBlock(response, snakeKey, camelKey);
  if (isUsable(respBlock)) {
    merged[snakeKey] = respBlock;
    if (camelKey && response?.[camelKey] != null) merged[camelKey] = response[camelKey];
    return;
  }
  if (isUsable(prevBlock)) {
    merged[snakeKey] = prevBlock;
    if (camelKey && prev?.[camelKey] != null) merged[camelKey] = prev[camelKey];
  }
}

function preserveField(merged, prev, response, snakeKey, camelKey, isPresent) {
  const respVal = pickBlock(response, snakeKey, camelKey);
  const prevVal = pickBlock(prev, snakeKey, camelKey);
  if (isPresent(respVal)) {
    merged[snakeKey] = respVal;
    if (camelKey && response?.[camelKey] != null) merged[camelKey] = response[camelKey];
    return;
  }
  if (isPresent(prevVal)) {
    merged[snakeKey] = prevVal;
    if (camelKey && prev?.[camelKey] != null) merged[camelKey] = prev[camelKey];
  }
}

function emitMergeTrace(payload) {
  try {
    // eslint-disable-next-line no-console
    console.info(LDS_PAYMENT_LINK_ORDER_MERGE_TRACE_TAG, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} prevOrder
 * @param {object|null|undefined} responseOrder
 * @param {{ emitTrace?: boolean }} [options]
 * @returns {object|null}
 */
export function mergeOrderAfterPaymentLinkResponse(prevOrder, responseOrder, options = {}) {
  if (!responseOrder || typeof responseOrder !== "object") {
    return prevOrder ?? null;
  }
  if (!prevOrder || typeof prevOrder !== "object") {
    return responseOrder;
  }

  const beforeFin = resolveLdsFinancialState(prevOrder);
  const responseFin = resolveLdsFinancialState(responseOrder);

  const merged = { ...prevOrder, ...responseOrder };

  preserveBlock(
    merged,
    prevOrder,
    responseOrder,
    "lds_partial_payment_display",
    "ldsPartialPaymentDisplay",
    hasUsablePartialDisplay
  );
  preserveBlock(
    merged,
    prevOrder,
    responseOrder,
    "lds_updated_order_total",
    "ldsUpdatedOrderTotal",
    hasUsableUpdatedTotal
  );
  preserveField(merged, prevOrder, responseOrder, "ourprice", "ourPrice", isNonemptyObject);
  preserveField(
    merged,
    prevOrder,
    responseOrder,
    "post_payment_checkout_audit",
    "postPaymentCheckoutAudit",
    isNonemptyObject
  );
  preserveField(
    merged,
    prevOrder,
    responseOrder,
    "passengersinfo",
    "passengersInfo",
    (value) => value != null
  );
  preserveField(merged, prevOrder, responseOrder, "offer", null, (value) => value != null);
  preserveField(merged, prevOrder, responseOrder, "order_response", null, isNonemptyObject);
  preserveField(
    merged,
    prevOrder,
    responseOrder,
    "packagepricedetails",
    null,
    (value) => value != null
  );
  preserveField(merged, prevOrder, responseOrder, "cruiseInfo", null, (value) => value != null);
  preserveField(merged, prevOrder, responseOrder, "cruise", null, (value) => value != null);

  if (Array.isArray(responseOrder.payments)) {
    merged.payments = responseOrder.payments;
  }

  const afterFin = resolveLdsFinancialState(merged);
  const hasPartialPaymentDisplay = hasUsablePartialDisplay(
    merged.lds_partial_payment_display ?? merged.ldsPartialPaymentDisplay
  );

  if (options.emitTrace !== false) {
    emitMergeTrace({
      orderId: String(merged.id ?? merged._id ?? merged.orderid ?? ""),
      beforeTotal: beforeFin.totalAmount,
      responseTotal: responseFin.totalAmount,
      afterTotal: afterFin.totalAmount,
      beforeRemaining: beforeFin.remainingBalance,
      responseRemaining: responseFin.remainingBalance,
      afterRemaining: afterFin.remainingBalance,
      hasPartialPaymentDisplay,
    });
  }

  return merged;
}
